import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Modal, Spin, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { ProductApi, ProductDetailDTO } from '../../apis/ProductApi';
import { OrderApi } from '../../apis/OrderApi';
import { CachedImage } from '../../components/CachedImage';
import { globalStore } from '../../GlobalStore';
import { PAGE_INLINE_PADDING_PX } from '../../constants/pageLayout';
import { PRODUCT_DETAIL_COVER_HEIGHT_PX } from '../../constants/productDetailLayout';
import './ProductDetailStyle.css';

const DEFAULT_SELLER_AVATAR =
  'https://superthem.oss-cn-shanghai.aliyuncs.com/support/sellerDefault.png';

const SpecTag: React.FC<{
  label: string;
  selected: boolean;
  onSelect: () => void;
}> = ({ label, selected, onSelect }) => (
  <span
    role="button"
    tabIndex={0}
    className={
      selected
        ? 'product-detail-spec-tag product-detail-spec-tag-selected'
        : 'product-detail-spec-tag'
    }
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect();
      }
    }}
  >
    {label}
  </span>
);

const ProductDescription: React.FC<{ text: string }> = ({ text }) => {
  const clampRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useLayoutEffect(() => {
    const el = clampRef.current;
    if (!el) {
      return;
    }
    setOverflow(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <>
      <div className="product-detail-desc-wrap">
        <div ref={clampRef} className="product-detail-desc-clamp">
          {text}
        </div>
        {overflow ? (
          <button
            type="button"
            className="product-detail-desc-more"
            onClick={() => setModalOpen(true)}
          >
            全部
          </button>
        ) : null}
      </div>
      <Modal
        title="规格描述"
        open={modalOpen}
        footer={null}
        onCancel={() => setModalOpen(false)}
        className="product-detail-desc-modal"
        destroyOnClose
      >
        {text}
      </Modal>
    </>
  );
};

const ProductDetail: React.FC = observer(() => {
  const { productId = '' } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const initialSpec = searchParams.get('spec') === 'trial' ? 'trial' : 'standard';

  const [product, setProduct] = useState<ProductDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [spec, setSpec] = useState<'trial' | 'standard'>(initialSpec);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    globalStore.forceWhiteHeader = true;
    return () => {
      globalStore.forceWhiteHeader = false;
    };
  }, []);

  useEffect(() => {
    if (!productId) {
      return;
    }
    setLoading(true);
    ProductApi.getProductDetail(productId).then((res) => {
      setProduct(res);
      setLoading(false);
    });
  }, [productId]);

  useEffect(() => {
    if (product && spec === 'trial' && product.trialEnabled === false) {
      setSpec('standard');
    }
  }, [product, spec]);

  const price = useMemo(() => {
    if (!product) {
      return 0;
    }
    return spec === 'trial' ? product.trialPrice : product.standardPrice;
  }, [product, spec]);

  const description = useMemo(() => {
    if (!product) {
      return '';
    }
    return spec === 'trial' ? product.trialDescription : product.standardDescription;
  }, [product, spec]);

  const headImages = useMemo(() => {
    if (!product) {
      return [] as string[];
    }
    if (product.headImages?.length) {
      return product.headImages;
    }
    if (product.headImageUrl) {
      return [product.headImageUrl];
    }
    return [];
  }, [product]);

  const handleBuy = async () => {
    if (!globalStore.isLoggedIn) {
      globalStore.loginModalVisible = true;
      return;
    }
    if (!product) {
      return;
    }
    setSubmitting(true);
    try {
      const order = await OrderApi.createOrder({
        productId: product.id,
        quantity: 1,
        specificationType: spec === 'trial' ? 0 : 1,
      });
      if (!order?.orderId) {
        message.error('创建订单失败');
        return;
      }
      const returnUrl = `${window.location.origin}${window.location.pathname}#/order-contract`;
      const prep = await OrderApi.preparePay({
        orderId: order.orderId,
        payScene: 'web_page',
        returnUrl,
        quitUrl: window.location.href,
      });
      const redirect = prep?.redirectUrl?.trim();
      if (!redirect) {
        message.error('未获取到收银台地址');
        return;
      }
      window.location.href = redirect;
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center' }}>
        商品不存在或已下架
      </div>
    );
  }

  const showTrial = product.trialEnabled !== false;
  const sellerAvatar = product.sellerAvatar?.trim() || DEFAULT_SELLER_AVATAR;

  return (
    <div
      style={{
        paddingTop: 72,
        paddingBottom: 48,
        paddingInline: PAGE_INLINE_PADDING_PX,
        maxWidth: 960,
        margin: '0 auto',
      }}
    >
      <div className="product-detail-main">
        <div className="product-detail-cover">
          {headImages[0] ? (
            <CachedImage
              url={headImages[0]}
              style={{
                width: '100%',
                maxWidth: 420,
                height: PRODUCT_DETAIL_COVER_HEIGHT_PX,
                borderRadius: 8,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : null}
        </div>
        <div className="product-detail-info">
          <h1 className="product-detail-title">{product.name}</h1>
          {product.sellerName ? (
            <div className="product-detail-seller">
              <CachedImage
                url={sellerAvatar}
                enablePreview={false}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
              <span>{product.sellerName}</span>
            </div>
          ) : null}
          <div className="product-detail-specs">
            {showTrial ? (
              <SpecTag
                label="体验版"
                selected={spec === 'trial'}
                onSelect={() => setSpec('trial')}
              />
            ) : null}
            <SpecTag
              label="标准版"
              selected={spec === 'standard'}
              onSelect={() => setSpec('standard')}
            />
          </div>
          <div className="product-detail-price">¥{price}</div>
          {description ? <ProductDescription text={description} /> : null}
          <Button
            type="primary"
            size="large"
            className="product-detail-buy-btn"
            loading={submitting}
            onClick={() => void handleBuy()}
          >
            立即购买
          </Button>
        </div>
      </div>
      {product.detailImages && product.detailImages.length > 0 ? (
        <div>
          <div className="product-detail-section-head">
            <h2 className="product-detail-section-title">商品详情</h2>
            <span className="product-detail-section-hint">
              更多商品详情和评价信息请下载圆领APP查看
            </span>
          </div>
          {product.detailImages.map((url) => (
            <CachedImage
              key={url}
              url={url}
              style={{ width: '100%', marginBottom: 8, display: 'block' }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
});

export default ProductDetail;
